/**
 * CELLHACK cart controller.
 *
 * Every add-to-cart and subscribe/one-time toggle on the site funnels through
 * `CellhackCart.add()` / `CellhackCart.changeSellingPlan()` below, which both
 * request Section Rendering API fragments and hand the response straight to
 * Dawn's existing `<cart-drawer>` element (`cart-drawer.js`): its
 * `renderContents()` already knows how to splice `#CartDrawer` and
 * `cart-icon-bubble` back into the page and open the drawer. We don't
 * reimplement that — we just reuse it from every call site:
 *   - static buttons: add `data-ch-add='{"items":[{"id":123,"quantity":1}]}'`
 *   - dynamic buttons (product-main bundle/selling-plan picker): call
 *     `window.CellhackCart.add(items)` directly from JS.
 */
(function () {
  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function sectionsToRender() {
    const drawer = getCartDrawer();
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((section) => section.section || section.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function request(url, extraBody) {
    const body = JSON.stringify({
      ...extraBody,
      sections: sectionsToRender(),
      sections_url: window.location.pathname,
    });

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status || parsedState.errors) {
          throw new Error(parsedState.description || parsedState.message || 'Cart request failed');
        }
        const drawer = getCartDrawer();
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(parsedState);
        }
        return parsedState;
      });
  }

  const CellhackCart = {
    /** items: [{ id: <variant id>, quantity: <n>, selling_plan?: <id> }] */
    add(items) {
      return request(window.routes.cart_add_url, { items }).catch((error) => {
        console.error('[cellhack-cart] add failed', error);
        window.alert(error.message || 'Could not add to cart. Please try again.');
      });
    },

    /** Swap a single cart line between one-time and subscribe, in place. */
    changeSellingPlan(line, sellingPlanId) {
      return request(window.routes.cart_change_url, {
        line,
        selling_plan: sellingPlanId || null,
      });
    },

    /** Swap one variant for another (e.g. 1 bottle to 2 bottles) and refresh the drawer. */
    swap(fromId, toId) {
      const updates = {};
      updates[fromId] = 0;
      updates[toId] = 1;
      return request(window.routes.cart_update_url, { updates }).catch((error) => {
        console.error('[cellhack-cart] swap failed', error);
      });
    },

    open() {
      const drawer = getCartDrawer();
      if (drawer && typeof drawer.open === 'function') drawer.open();
    },
  };

  window.CellhackCart = CellhackCart;

  document.addEventListener('click', (event) => {
    const addTrigger = event.target.closest('[data-ch-add]');
    if (addTrigger) {
      event.preventDefault();
      if (addTrigger.hasAttribute('aria-disabled')) return;
      let payload;
      try {
        payload = JSON.parse(addTrigger.getAttribute('data-ch-add'));
      } catch (error) {
        console.error('[cellhack-cart] invalid data-ch-add JSON', error);
        return;
      }
      addTrigger.setAttribute('aria-disabled', 'true');
      CellhackCart.add(payload.items).finally(() => addTrigger.removeAttribute('aria-disabled'));
      return;
    }

    const swapTrigger = event.target.closest('[data-ch-swap]');
    if (swapTrigger) {
      event.preventDefault();
      if (swapTrigger.hasAttribute('aria-disabled')) return;
      let swap;
      try {
        swap = JSON.parse(swapTrigger.getAttribute('data-ch-swap'));
      } catch (error) {
        return;
      }
      swapTrigger.setAttribute('aria-disabled', 'true');
      CellhackCart.swap(swap.from, swap.to).finally(() => swapTrigger.removeAttribute('aria-disabled'));
      return;
    }

    const subToggle = event.target.closest('[data-ch-sub-toggle]');
    if (subToggle) {
      event.preventDefault();
      if (subToggle.hasAttribute('aria-disabled')) return;
      const line = subToggle.getAttribute('data-line');
      const isSubscribed = subToggle.getAttribute('aria-pressed') === 'true';
      const sellingPlanId = isSubscribed ? null : subToggle.getAttribute('data-selling-plan');
      subToggle.setAttribute('aria-disabled', 'true');
      CellhackCart.changeSellingPlan(line, sellingPlanId).finally(() => subToggle.removeAttribute('aria-disabled'));
    }
  });
})();

/* "How did you hear about us?" saves to the cart so it lands on the order. */
document.addEventListener('change', (event) => {
  const select = event.target.closest && event.target.closest('[data-ch-source-select]');
  if (!select) return;
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { 'How did you hear about us': select.value } }),
  }).catch(() => {});
});
